import {
  ArchiveIcon,
  CheckCircleIcon,
  DeleteIcon,
  EditIcon,
  MergeIcon,
  RefreshIcon,
  type ResourceMenuItem,
} from '@all-manager/design-system-v2';
import React, { useMemo, useState } from 'react';
import type { SubTopicDatabase, SubTopicWithLastActivityWithIdDatabase } from '../../../../types';
import { useArchiveSubTopic } from '../../api/useArchiveSubTopic';
import { useCloseSubTopic } from '../../api/useCloseSubTopic';
import { useReopenSubTopic } from '../../api/useReopenSubTopic';

export type SubTopicActionModal = 'close' | 'archive' | 'delete' | 'update' | 'reopen' | 'merge' | null;

export type SubTopicActionsProps = {
  subTopic: SubTopicDatabase | SubTopicWithLastActivityWithIdDatabase;
  size?: 'small' | 'medium' | 'large';
};

export function computeSubTopicActions(
  subTopic: { state?: string | null; archived_at?: string | null },
  onActionClick: (type: SubTopicActionModal) => void
): (ResourceMenuItem | null | undefined | false)[] {
  const isClosed = subTopic.state === 'closed';
  const isArchived = Boolean(subTopic.archived_at);

  return [
    isClosed
      ? {
          id: 'reopen',
          label: 'Réouvrir',
          icon: React.createElement(RefreshIcon, { fontSize: 'small' }),
          onClick: () => onActionClick('reopen'),
        }
      : {
          id: 'close',
          label: 'Clôturer',
          icon: React.createElement(CheckCircleIcon, { fontSize: 'small' }),
          onClick: () => onActionClick('close'),
        },
    !isArchived && {
      id: 'archive',
      label: 'Archiver',
      icon: React.createElement(ArchiveIcon, { fontSize: 'small' }),
      onClick: () => onActionClick('archive'),
    },
    { type: 'divider', id: 'div-1' },
    {
      id: 'update',
      label: 'Modifier',
      icon: React.createElement(EditIcon, { fontSize: 'small' }),
      onClick: () => onActionClick('update'),
    },
    {
      id: 'merge',
      label: 'Fusionner',
      icon: React.createElement(MergeIcon, { fontSize: 'small' }),
      onClick: () => onActionClick('merge'),
    },
    { type: 'divider', id: 'div-2' },
    {
      id: 'delete',
      label: 'Supprimer',
      icon: React.createElement(DeleteIcon, { fontSize: 'small', color: 'error' }),
      onClick: () => onActionClick('delete'),
    },
  ];
}

export function useSubTopicActions(props: SubTopicActionsProps) {
  const [activeModal, setActiveModal] = useState<SubTopicActionModal>(null);

  const { mutateAsync: closeSubTopic } = useCloseSubTopic();
  const { mutateAsync: archiveSubTopic } = useArchiveSubTopic();
  const { mutateAsync: reopenSubTopic } = useReopenSubTopic();

  function handleActionClick(type: SubTopicActionModal) {
    setActiveModal(type);
  }

  function handleCloseModal() {
    setActiveModal(null);
  }

  async function handleConfirmClose() {
    await closeSubTopic(props.subTopic.id);
  }

  async function handleConfirmReopen() {
    await reopenSubTopic(props.subTopic.id);
  }

  async function handleConfirmArchive() {
    await archiveSubTopic(props.subTopic.id);
  }

  const actions = useMemo(() => {
    return computeSubTopicActions(props.subTopic, handleActionClick);
  }, [props.subTopic.state, props.subTopic.archived_at]);

  return {
    activeModal,
    actions,
    handleActionClick,
    handleCloseModal,
    handleConfirmClose,
    handleConfirmReopen,
    handleConfirmArchive,
  };
}
